/**
 * The rolling frame buffer.
 *
 * This is the whole trick of the app: by the time the shutter is pressed, the
 * seconds spent framing the shot have already been recorded, and in those
 * seconds the phone has been in a dozen different places. The room behind it
 * was photographed the entire time.
 *
 * Frames are held as canvases at composite resolution, so the buffer is real
 * memory — `frames × composite² × 4` bytes. Canvases are recycled rather than
 * reallocated, which on a phone is the difference between a steady preview and
 * one that stutters every time the garbage collector catches up.
 */
import { mk, drawCtx } from '../vision/image';

export interface BufferOptions {
  /** How many frames to keep. */
  capacity: number;
  /** Milliseconds between grabs. */
  interval: number;
  /** Longest edge of each stored frame. */
  longEdge: number;
}

export class FrameBuffer {
  #frames: HTMLCanvasElement[] = [];
  #recycled: HTMLCanvasElement[] = [];
  #timer: number | null = null;
  #opts: BufferOptions;
  #w = 0;
  #h = 0;
  #paused = false;
  #source: (CanvasImageSource & { videoWidth?: number; videoHeight?: number }) | null = null;
  #onChange: ((count: number, capacity: number) => void) | null = null;

  constructor(opts: BufferOptions) {
    this.#opts = opts;
  }

  get frames(): readonly HTMLCanvasElement[] {
    return this.#frames;
  }

  get count(): number {
    return this.#frames.length;
  }

  get capacity(): number {
    return this.#opts.capacity;
  }

  get width(): number {
    return this.#w;
  }

  get height(): number {
    return this.#h;
  }

  /** Seconds of history the buffer currently holds. */
  get seconds(): number {
    return (this.#opts.capacity * this.#opts.interval) / 1000;
  }

  onChange(handler: ((count: number, capacity: number) => void) | null): void {
    this.#onChange = handler;
  }

  /** Fit a source's aspect ratio into the configured long edge. */
  setSourceSize(sourceW: number, sourceH: number): void {
    if (!sourceW || !sourceH) return;
    // Never larger than the camera delivers: enlarging adds nothing but
    // memory, and it shifts the detector's scores on the frames it reads.
    const long = Math.min(this.#opts.longEdge, Math.max(sourceW, sourceH));
    const [w, h] =
      sourceH >= sourceW
        ? [Math.round((sourceW * long) / sourceH), long]
        : [long, Math.round((sourceH * long) / sourceW)];
    if (w === this.#w && h === this.#h) return;
    this.#w = w;
    this.#h = h;
    this.clear();
  }

  configure(opts: Partial<BufferOptions>): void {
    const wasRunning = this.#timer !== null;
    this.#opts = { ...this.#opts, ...opts };
    this.clear();
    if (wasRunning) this.restart();
  }

  start(source: CanvasImageSource & { videoWidth?: number; videoHeight?: number }): void {
    this.stop();
    const tick = () => {
      if (this.#paused) return;
      const sw = source.videoWidth ?? 0;
      const sh = source.videoHeight ?? 0;
      if (!sw || !sh) return;
      this.setSourceSize(sw, sh);
      this.push(source);
    };
    this.#source = source;
    this.#timer = window.setInterval(tick, this.#opts.interval);
    tick();
  }

  restart(): void {
    if (this.#source) this.start(this.#source);
  }

  stop(): void {
    if (this.#timer !== null) window.clearInterval(this.#timer);
    this.#timer = null;
  }

  /** Hold the buffer still without dropping it — used while a capture runs. */
  setPaused(paused: boolean): void {
    this.#paused = paused;
  }

  push(source: CanvasImageSource): void {
    if (!this.#w || !this.#h) return;
    const canvas = this.#take();
    const ctx = drawCtx(canvas);
    ctx.drawImage(source, 0, 0, this.#w, this.#h);
    this.#frames.push(canvas);
    while (this.#frames.length > this.#opts.capacity) {
      const dropped = this.#frames.shift();
      if (dropped) this.#recycled.push(dropped);
    }
    this.#onChange?.(this.#frames.length, this.#opts.capacity);
  }

  #take(): HTMLCanvasElement {
    const reused = this.#recycled.pop();
    if (reused && reused.width === this.#w && reused.height === this.#h) return reused;
    return mk(this.#w, this.#h);
  }

  /** A stable copy of the buffer, oldest first, for a capture to work from. */
  snapshot(): HTMLCanvasElement[] {
    return this.#frames.slice();
  }

  /**
   * Replace the buffer wholesale. Used by clip import, where the frames come
   * from a file rather than the camera.
   */
  adopt(frames: HTMLCanvasElement[], w: number, h: number): void {
    this.clear();
    this.#w = w;
    this.#h = h;
    this.#frames = frames;
    this.#onChange?.(this.#frames.length, this.#opts.capacity);
  }

  clear(): void {
    // Canvases are dropped rather than pooled here: a clear usually follows a
    // resolution change, which makes every pooled canvas the wrong size.
    this.#frames = [];
    this.#recycled = [];
    this.#onChange?.(0, this.#opts.capacity);
  }
}
